import type { InventoryResourceType } from "@@/shared/acquire/resources/inventory-resource";
import type { ResourceAccommodation } from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation";
import { resourceAccommodationDefinitionDataZodObject } from "@@/shared/acquire/resources/resource-accommodation/resource-accommodation-definition-data";
import type { ResourceDefinitionOf } from "@@/shared/acquire/resources/resource-definition-helpers";
import { resourceFoundationIdZodSchema } from "@@/shared/acquire/resources/resource-foundation-id";
import type { ResourceGeneric } from "@@/shared/acquire/resources/resource-generic";
import { resourceGenericDefinitionDataZodObject } from "@@/shared/acquire/resources/resource-generic-definition";
import {
    resourceIdFactory,
    resourceIdZodSchema,
} from "@@/shared/acquire/resources/resource-id";
import type { ResourceReserved } from "@@/shared/acquire/resources/resource-reserved";
import { resourceReservedDefinitionDataZodObject } from "@@/shared/acquire/resources/resource-reserved-definition";
import type { ResourceTable } from "@@/shared/acquire/resources/resource-table";
import { resourceTableDefinitionDataZodObject } from "@@/shared/acquire/resources/resource-table-definition";
import { providerIdZodSchema } from "@@/shared/providers/provider-id";
import { assertTypeMatch } from "@@/utilities/type-helpers";
import type { IsEqual } from "type-fest";
import { z } from "zod";

const resourceDefinitionZodObject = z.object({
    _id: resourceIdZodSchema,
    _type: z.literal("RESOURCE_DEFINITION"),
    foundationId: resourceFoundationIdZodSchema,
    hasOverrides: z.boolean(),
    providerId: providerIdZodSchema,
    data: z.discriminatedUnion("_type", [
        resourceGenericDefinitionDataZodObject,
        resourceTableDefinitionDataZodObject,
        resourceAccommodationDefinitionDataZodObject,
        resourceReservedDefinitionDataZodObject,
    ]),
});

const resourceAccommodationDefinitionZodObject = resourceDefinitionZodObject
    .omit({
        data: true,
    })
    .and(z.object({ data: resourceAccommodationDefinitionDataZodObject }));

type ResourceDefinition = z.infer<typeof resourceDefinitionZodObject>;

type ResourceDefinitionData<T extends ResourceDefinition["data"]["_type"]> =
    Extract<ResourceDefinition["data"], { _type: T }>;

// make sure we've got all type types from inventory resource types
// covered in the ResourceDefinition.type property
assertTypeMatch<
    IsEqual<InventoryResourceType, ResourceDefinition["data"]["_type"]>
>();

const _resourceDefinitionFactoryParamsZodObject =
    resourceDefinitionZodObject.omit({
        _id: true,
        _type: true,
    });
type ResourceDefinitionDataFactoryParams = z.infer<
    typeof _resourceDefinitionFactoryParamsZodObject.shape.data
>;
type ResourceDefinitionFactoryParams = z.infer<
    typeof _resourceDefinitionFactoryParamsZodObject
>;

function resourceDefinitionFactory(
    ofType: "RESOURCE_ACCOMMODATION",
    params: ResourceDefinitionFactoryParams,
): ResourceDefinitionOf<ResourceAccommodation>;
function resourceDefinitionFactory(
    ofType: "RESOURCE_GENERIC",
    params: ResourceDefinitionFactoryParams,
): ResourceDefinitionOf<ResourceGeneric>;
function resourceDefinitionFactory(
    ofType: "RESOURCE_TABLE",
    params: ResourceDefinitionFactoryParams,
): ResourceDefinitionOf<ResourceTable>;
function resourceDefinitionFactory(
    ofType: "RESOURCE_RESERVED",
    params: ResourceDefinitionFactoryParams,
): ResourceDefinitionOf<ResourceReserved>;
function resourceDefinitionFactory(
    _ofType: InventoryResourceType,
    params: ResourceDefinitionFactoryParams,
): ResourceDefinition {
    const { data, ..._params } = params;
    const _data: Extract<
        ResourceDefinitionDataFactoryParams,
        { _type: InventoryResourceType }
    > = data;
    const ResourceDefinition: ResourceDefinition = {
        ...params,
        _id: resourceIdFactory(),
        _type: "RESOURCE_DEFINITION",
        data: _data,
    };
    return ResourceDefinition;
}

export {
    resourceAccommodationDefinitionZodObject,
    resourceDefinitionFactory,
    resourceDefinitionZodObject,
};
export type { ResourceDefinition, ResourceDefinitionData };
